/**
 * New Trust Request modal — the primary institution interaction primitive.
 *
 * Users pick a request TYPE (which drives the underlying workflow), enter
 * the consumer's hint (or paste a consumer_id) and submit. For
 * `trust_package`, they also pick a workflow template.
 *
 * Uses shadcn Dialog + toast for feedback. Tenant is derived server-side
 * from the caller — the modal never sends tenant_id.
 */
import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { X, Send, Package, KeyRound, Fingerprint, ShieldCheck, ClipboardCheck, BookOpen, GraduationCap, Dice5 } from "lucide-react";

const NAVY = "#0A1F44";
const GOLD = "#C9A24A";

const TYPES = [
  { id: "consent",                 label: "Consent",                 icon: KeyRound,      hint: "Ask consumer to grant purpose-bound access to data." },
  { id: "identity_verification",   label: "Identity Verification",   icon: Fingerprint,   hint: "Confirm the consumer is who they claim to be." },
  { id: "kyc",                     label: "KYC",                     icon: ShieldCheck,   hint: "Full onboarding: identity + sanctions + PEP + adverse media." },
  { id: "affordability",           label: "Affordability",           icon: ClipboardCheck,hint: "Evidence that this product fits the consumer's means." },
  { id: "product_understanding",   label: "Product Understanding",   icon: BookOpen,      hint: "Prove the consumer understood before they signed." },
  { id: "learning_assignment",     label: "Learning Assignment",     icon: GraduationCap, hint: "Ask consumer to complete a specific lesson." },
  { id: "responsible_gaming_review", label: "Responsible Gaming Review", icon: Dice5,     hint: "Threshold-triggered check for gaming operators." },
  { id: "trust_package",           label: "Trust Package",           icon: Package,       hint: "Bundle multiple requests as one consumer-facing interaction." },
];

const TEMPLATES = [
  { id: "customer-onboarding",    label: "Customer Onboarding" },
  { id: "investor-onboarding",    label: "Investor Onboarding" },
  { id: "gaming-registration",    label: "Gaming Registration" },
  { id: "kyc-verification",       label: "KYC Verification" },
  { id: "identity-verification",  label: "Identity Verification" },
];

export default function NewTrustRequestModal({ onClose, onCreated }) {
  const [type, setType] = useState("consent");
  const [purpose, setPurpose] = useState("");
  const [description, setDescription] = useState("");
  const [consumerHint, setConsumerHint] = useState("");
  const [consumerId, setConsumerId] = useState("");
  const [consumerDisplayName, setConsumerDisplayName] = useState("");
  const [workflowTemplate, setWorkflowTemplate] = useState("customer-onboarding");
  const [expiresInDays, setExpiresInDays] = useState(14);
  const [submitting, setSubmitting] = useState(false);

  const isBundle = type === "trust_package";
  const canSubmit = purpose.trim().length >= 3 && (consumerId.trim() || consumerHint.trim());

  async function submit(e) {
    e.preventDefault();
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      const payload = {
        request_type: type,
        purpose: purpose.trim(),
        purpose_description: description.trim(),
        consumer_id: consumerId.trim() || null,
        consumer_hint: consumerHint.trim() || null,
        consumer_display_name: consumerDisplayName.trim() || null,
        expires_in_days: Number(expiresInDays) || 14,
        send_immediately: true,
      };
      if (isBundle) {
        payload.workflow_template_id = workflowTemplate;
      }
      const { data } = await api.post("/trust-requests", payload);
      toast.success(`Trust Request ${data.request_id} sent`);
      onCreated?.(data);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to create Trust Request";
      toast.error(String(msg));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      data-testid="new-request-modal"
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4"
      onClick={onClose}
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full my-8 p-6 space-y-5"
      >
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-semibold" style={{ color: GOLD }}>
              New Trust Request
            </div>
            <h2 className="font-display text-2xl font-semibold" style={{ color: NAVY }}>
              Send a Trust Request
            </h2>
          </div>
          <button
            type="button"
            data-testid="close-new-request-modal"
            onClick={onClose}
            className="p-2 rounded-full hover:bg-slate-100 text-slate-500"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Type picker */}
        <fieldset className="space-y-2">
          <legend className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">
            Request type
          </legend>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="new-request-type-picker">
            {TYPES.map((t) => {
              const Icon = t.icon;
              const active = t.id === type;
              return (
                <button
                  key={t.id}
                  type="button"
                  data-testid={`type-option-${t.id}`}
                  onClick={() => setType(t.id)}
                  className={`text-left rounded-xl border p-3 transition ${
                    active ? "border-slate-800 shadow-sm bg-slate-50" : "border-slate-200 hover:border-slate-300"
                  }`}
                  title={t.hint}
                >
                  <Icon className="w-4 h-4 mb-1" style={{ color: active ? NAVY : GOLD }} />
                  <div className="text-[12px] font-semibold" style={{ color: NAVY }}>{t.label}</div>
                </button>
              );
            })}
          </div>
        </fieldset>

        {/* Workflow template for trust_package */}
        {isBundle && (
          <label className="block space-y-1">
            <span className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">
              Workflow template
            </span>
            <select
              data-testid="new-request-template"
              value={workflowTemplate}
              onChange={(e) => setWorkflowTemplate(e.target.value)}
              className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm"
            >
              {TEMPLATES.map((t) => (
                <option key={t.id} value={t.id}>{t.label}</option>
              ))}
            </select>
          </label>
        )}

        {/* Purpose */}
        <label className="block space-y-1">
          <span className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">
            Purpose <span className="text-red-500">*</span>
          </span>
          <input
            data-testid="new-request-purpose"
            required
            value={purpose}
            onChange={(e) => setPurpose(e.target.value)}
            placeholder="e.g. Consent for KYC on new account"
            className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm"
          />
        </label>

        <label className="block space-y-1">
          <span className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">
            Description
          </span>
          <textarea
            data-testid="new-request-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            placeholder="Explain to the consumer why you are asking. This appears in the wallet."
            className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm"
          />
        </label>

        {/* Consumer + timing */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <label className="block space-y-1 md:col-span-2">
            <span className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">
              Consumer ID <em className="normal-case text-slate-400">(or hint below)</em>
            </span>
            <input
              data-testid="new-request-consumer-id"
              value={consumerId}
              onChange={(e) => setConsumerId(e.target.value)}
              placeholder="csr_xxxxxxxxxxxx"
              className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm font-mono"
            />
          </label>
          <label className="block space-y-1">
            <span className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">
              Expires in
            </span>
            <div className="flex items-center gap-1">
              <input
                data-testid="new-request-expires"
                type="number"
                min="1"
                max="365"
                value={expiresInDays}
                onChange={(e) => setExpiresInDays(e.target.value)}
                className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm"
              />
              <span className="text-[12px] text-slate-500">days</span>
            </div>
          </label>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="block space-y-1">
            <span className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">
              Consumer hint <em className="normal-case text-slate-400">(email / phone / reference)</em>
            </span>
            <input
              data-testid="new-request-consumer-hint"
              value={consumerHint}
              onChange={(e) => setConsumerHint(e.target.value)}
              placeholder="consumer@example.com"
              className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm"
            />
          </label>
          <label className="block space-y-1">
            <span className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">
              Consumer display name
            </span>
            <input
              data-testid="new-request-consumer-name"
              value={consumerDisplayName}
              onChange={(e) => setConsumerDisplayName(e.target.value)}
              placeholder="Ms Naledi K."
              className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm"
            />
          </label>
        </div>

        {/* Submit */}
        <div className="flex items-center justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="text-[13px] font-semibold px-4 py-2.5 rounded-xl text-slate-600 hover:bg-slate-100"
          >
            Cancel
          </button>
          <button
            type="submit"
            data-testid="submit-new-request"
            disabled={!canSubmit || submitting}
            className="inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-[13px] font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: NAVY }}
          >
            <Send className="w-4 h-4" />
            {submitting ? "Sending…" : "Send Trust Request"}
          </button>
        </div>
      </form>
    </div>
  );
}
